import React, { useEffect, useState } from 'react';

import ModalPortal from '../components/modal-portal';
import { useVisualViewportBox } from '../components/use-visual-viewport-box';
import ClassificationBreakdown from './classification-breakdown';
import PencilIcon from './pencil-icon';
import ScoringMatchEntry from './scoring-match-entry';
import ScoringMatchSides from './scoring-match-sides';
import { currentWinner, initialSets, isDrawn, isInProgress } from './scoring-match-draft';
import {
  isWalkover,
  opponentName,
  opponentPlayerIds,
  opponentRating,
  partnerName,
  partnerPlayerIds,
  playerOutcome,
  playerPointsLabel,
  playerResultLabel,
  type PlayerOutcome,
} from './scoring-match-query';
import ScoringPlayerLink from './scoring-player-link';
import {
  deltaTone,
  formatBuchholz,
  formatDelta,
  smallPointsBalance,
  type MatchItem,
  type ScoringSystem,
  type StandingRow,
} from './scoring-types';

// Wpisywanie wyniku wymaga tokenu z linku organizatora. Publiczny podgląd kolejki
// go nie ma — wtedy mecz otwiera się tylko do odczytu.
interface EntryAccess {
  token: string;
  canSubmit: boolean;
  onSaved: () => void;
}

interface Props {
  player: StandingRow;
  matches: MatchItem[];
  showPoints: boolean;
  showPlacement?: boolean;
  ratingNote?: string;
  scoring?: ScoringSystem;
  doubles?: boolean;
  entry?: EntryAccess;
  // Do ilu i jak gramy — pokazywane nad formularzem wyniku, tak jak w oknie meczu.
  formatLabel?: string;
  // Ile punktów daje pauza w americano; null poza americano — wtedy pauz nie ma.
  restingRunPoints?: number | null;
  big?: boolean;
  onClose: () => void;
}

type ListEntry = { kind: 'match'; runNumber: number | null; match: MatchItem } | { kind: 'rest'; runNumber: number };

// Pauza staje w liście tam, gdzie wypadła jej runda — czytający widzi, kiedy siedział
// i co za to dostał, zamiast szukać różnicy w punktach.
function listEntries(matches: MatchItem[], restedRunNumbers: number[]): ListEntry[] {
  const entries: ListEntry[] = [
    ...matches.map((match): ListEntry => ({ kind: 'match', runNumber: match.runNumber ?? null, match })),
    ...restedRunNumbers.map((runNumber): ListEntry => ({ kind: 'rest', runNumber })),
  ];

  if (restedRunNumbers.length === 0) {
    return entries;
  }

  return entries.sort((a, b) => (a.runNumber ?? 0) - (b.runNumber ?? 0));
}

function resultClass(outcome: PlayerOutcome | null): string {
  if (outcome === null) {
    return 'scoring-modal__result';
  }

  return `scoring-modal__result scoring-modal__result--${outcome}`;
}

interface StatProps {
  label: string;
  value: React.ReactNode;
}

const ModalStat: React.FC<StatProps> = ({ label, value }) => (
  <div className="scoring-modal__stat">
    <span className="scoring-modal__stat-label">{label}</span>
    <span className="scoring-modal__stat-value">{value}</span>
  </div>
);

export const ScoringPlayerMatchesModal: React.FC<Props> = ({
  player,
  matches,
  showPoints,
  showPlacement = true,
  ratingNote = 'ranking po tej kolejce',
  scoring,
  doubles = false,
  entry,
  formatLabel,
  restingRunPoints = null,
  big = false,
  onClose,
}) => {
  const [pickedMatchId, setPickedMatchId] = useState<number | null>(null);
  const viewportBox = useVisualViewportBox();
  const restedRunNumbers = restingRunPoints === null ? [] : player.restedRunNumbers ?? [];

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const pickedMatch = matches.find((match) => match.id === pickedMatchId) ?? null;

  function backToList(): void {
    setPickedMatchId(null);
  }

  function handleSaved(): void {
    setPickedMatchId(null);
    entry?.onSaved();
  }

  function matchRow(match: MatchItem): JSX.Element {
    return (
      <li key={match.id} className="scoring-modal__row">
        <button
          type="button"
          className="scoring-modal__match"
          data-testid={`scoring-player-modal-match-${match.id}`}
          onClick={() => setPickedMatchId(match.id)}
        >
          <span className="scoring-modal__opponent">
            vs{' '}
            <ScoringPlayerLink
              name={opponentName(match, player.leaguePlayerId)}
              playerIds={opponentPlayerIds(match, player.leaguePlayerId)}
              doubles={doubles}
            />
            {opponentRating(match, player.leaguePlayerId) !== null && (
              <span className="scoring-modal__opponent-rating">
                {opponentRating(match, player.leaguePlayerId)}
              </span>
            )}
          </span>
          {partnerName(match, player.leaguePlayerId) !== null && (
            <span
              className="scoring-modal__partner"
              data-testid={`scoring-player-modal-partner-${match.id}`}
            >
              w parze z{' '}
              <ScoringPlayerLink
                name={partnerName(match, player.leaguePlayerId) ?? ''}
                playerIds={partnerPlayerIds(match, player.leaguePlayerId)}
                doubles={doubles}
              />
            </span>
          )}
          <span
            className={
              isInProgress(match)
                ? 'scoring-modal__result scoring-modal__result--partial'
                : resultClass(playerOutcome(match, player.leaguePlayerId))
            }
            data-testid={`scoring-player-modal-result-${match.id}`}
          >
            {playerResultLabel(match, player.leaguePlayerId)}
            {isWalkover(match) && <span className="scoring-modal__walkover">(wo)</span>}
          </span>
          <span
            className="scoring-modal__points"
            data-testid={`scoring-player-modal-points-${match.id}`}
          >
            {playerPointsLabel(match, scoring, player.leaguePlayerId) ?? '—'}
          </span>
        </button>
        {entry?.canSubmit && (
          <button
            type="button"
            className="scoring-match__edit"
            data-testid={`scoring-player-modal-edit-${match.id}`}
            aria-label="Wpisz wynik"
            title="Wpisz wynik"
            onClick={() => setPickedMatchId(match.id)}
          >
            <PencilIcon />
          </button>
        )}
      </li>
    );
  }

  return (
    <ModalPortal>
      <div className="scoring-modal__backdrop" style={viewportBox} data-testid="scoring-player-modal" onClick={onClose}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Mecze — ${player.name}`}
          className={big ? 'scoring-modal scoring-modal--big' : 'scoring-modal'}
          onClick={(event) => event.stopPropagation()}
        >
          <header className="scoring-modal__head">
            {pickedMatch !== null && (
              <button
                type="button"
                className="scoring-modal__back"
                data-testid="scoring-player-modal-back"
                onClick={backToList}
              >
                ← Mecze
              </button>
            )}
            <h3 className="scoring-modal__title">
              <ScoringPlayerLink
                name={player.name}
                playerIds={player.playerIds}
                doubles={doubles}
              />
            </h3>
            <button
              type="button"
              className="scoring-modal__close"
              data-testid="scoring-player-modal-close"
              aria-label="Zamknij"
              onClick={onClose}
            >
              ✕
            </button>
          </header>

          {pickedMatch !== null && entry && formatLabel && (
            <p className="scoring-modal__rules" data-testid="scoring-player-modal-rules">
              {formatLabel}
            </p>
          )}

          {pickedMatch !== null ? (
            entry ? (
              <ScoringMatchEntry
                match={pickedMatch}
                token={entry.token}
                canSubmit={entry.canSubmit}
                scoring={scoring}
                onSaved={handleSaved}
                onCancel={backToList}
              />
            ) : (
              <div
                className="scoring-modal__entry"
                data-testid={`scoring-modal-entry-${pickedMatch.id}`}
              >
                <ScoringMatchSides
                  match={pickedMatch}
                  sets={initialSets(pickedMatch)}
                  winner={currentWinner(pickedMatch)}
                  scoring={scoring}
                  drawn={isDrawn(pickedMatch)}
                />
              </div>
            )
          ) : (
            <>
              <p className="scoring-modal__rating" data-testid="scoring-player-modal-rating">
                <span className="scoring-modal__rating-value">{player.rating ?? '—'}</span>
                {player.ratingDelta != null && (
                  <span className={deltaTone(player.ratingDelta)}>
                    {formatDelta(player.ratingDelta)}
                  </span>
                )}
                <span className="scoring-modal__rating-note">{ratingNote}</span>
              </p>

              <div className="scoring-modal__stats" data-testid="scoring-player-modal-stats">
                <ModalStat label="Miejsce" value={player.rank} />
                <ModalStat label="Mecze" value={`${player.wins}–${player.losses}`} />
                {player.draws != null && <ModalStat label="Remisy" value={player.draws} />}
                <ModalStat label="Sety" value={`${player.setsWon}:${player.setsLost}`} />
                <ModalStat
                  label="Lotki"
                  value={
                    <span className={deltaTone(smallPointsBalance(player))}>
                      {formatDelta(smallPointsBalance(player))}
                    </span>
                  }
                />
                {restedRunNumbers.length > 0 && (
                  <ModalStat label="Pauzy" value={restedRunNumbers.length} />
                )}
                {showPoints && <ModalStat label="Pkt" value={player.points} />}
                {player.buchholz != null && <ModalStat label="Bh" value={formatBuchholz(player.buchholz)} />}
                {showPlacement && <ModalStat label="Klas." value={player.placementPoints} />}
              </div>

              {showPlacement && (
                <ClassificationBreakdown
                  total={player.placementPoints}
                  lines={player.classificationBreakdown ?? []}
                />
              )}

              {matches.length === 0 ? (
                <p className="scoring-modal__empty">Ten zawodnik nie ma meczów w tej grupie.</p>
              ) : (
                <ul className="scoring-modal__list">
                  {listEntries(matches, restedRunNumbers).map((item) =>
                    item.kind === 'rest' ? (
                      <li
                        key={`rest-${item.runNumber}`}
                        className="scoring-modal__rest"
                        data-testid={`scoring-player-modal-rest-${item.runNumber}`}
                      >
                        <span className="scoring-modal__rest-label">Pauza · runda {item.runNumber}</span>
                        <span className="scoring-modal__points">+{restingRunPoints} pkt</span>
                      </li>
                    ) : (
                      matchRow(item.match)
                    ),
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ScoringPlayerMatchesModal;
